import { useState } from 'react';
import { CONTACT_API_URL, DEMO_PRODUCT_OPTIONS } from '../config/site';

const INITIAL = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  serviceType: '',
};

export default function ContactForm({
  defaultServiceType = '',
  defaultSubject = '',
  subjectPrefix = '',
  showProductSelect = false,
  submitLabel = 'Send message',
}) {
  const [form, setForm] = useState({
    ...INITIAL,
    serviceType: defaultServiceType,
    subject: defaultSubject,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.email.trim()) next.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) next.email = 'Enter a valid email';
    if (!form.phone.trim()) next.phone = 'Phone is required';
    if (!form.subject.trim()) next.subject = 'Subject is required';
    if (!form.message.trim()) next.message = 'Message is required';
    if (showProductSelect && !form.serviceType) next.serviceType = 'Select a product or service';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setStatus({ type: '', message: '' });

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      subject: subjectPrefix ? `${subjectPrefix}${form.subject.trim()}` : form.subject.trim(),
      message: form.message.trim(),
      serviceType: form.serviceType || 'General inquiry',
    };

    try {
      const response = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you — we will get back to you on business days.',
        });
        setForm({ ...INITIAL, serviceType: defaultServiceType });
        setErrors({});
        return;
      }

      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        setStatus({
          type: 'error',
          message: data.message || 'Too many requests. Please try again later.',
        });
        return;
      }

      throw new Error('Request failed');
    } catch {
      setStatus({
        type: 'error',
        message: 'Could not send your message. Email us directly at info@grahmantree.com.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form__grid">
        <label className="field">
          <span>Full name *</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name && <em className="field-error">{errors.name}</em>}
        </label>
        <label className="field">
          <span>Phone *</span>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
            aria-invalid={Boolean(errors.phone)}
          />
          {errors.phone && <em className="field-error">{errors.phone}</em>}
        </label>
        <label className="field field--full">
          <span>Email *</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            aria-invalid={Boolean(errors.email)}
          />
          {errors.email && <em className="field-error">{errors.email}</em>}
        </label>
        {showProductSelect && (
          <label className="field field--full">
            <span>Product / interest *</span>
            <select
              value={form.serviceType}
              onChange={(e) => setField('serviceType', e.target.value)}
              aria-invalid={Boolean(errors.serviceType)}
            >
              <option value="">Select one</option>
              {DEMO_PRODUCT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.label}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.serviceType && <em className="field-error">{errors.serviceType}</em>}
          </label>
        )}
        <label className="field field--full">
          <span>Subject *</span>
          <input
            type="text"
            value={form.subject}
            onChange={(e) => setField('subject', e.target.value)}
            aria-invalid={Boolean(errors.subject)}
          />
          {errors.subject && <em className="field-error">{errors.subject}</em>}
        </label>
        <label className="field field--full">
          <span>Message *</span>
          <textarea
            rows={5}
            value={form.message}
            onChange={(e) => setField('message', e.target.value)}
            aria-invalid={Boolean(errors.message)}
          />
          {errors.message && <em className="field-error">{errors.message}</em>}
        </label>
      </div>
      {status.message && (
        <p className={`form-status form-status--${status.type}`} role="status">
          {status.message}
        </p>
      )}
      <button type="submit" className="btn btn--glow" disabled={loading}>
        {loading ? 'Sending…' : submitLabel}
      </button>
    </form>
  );
}
